/**
 * PartnerScoringPanel — Prime download + Prime upload + Consolidated
 * download, plus the live submissions roster.
 *
 * Placement: below the Extracted Requirements section in Opportunity Intake.
 * Per-partner upload buttons live in the Teaming Partners section (above)
 * and use the sibling component `PartnerScoringUploadButton`.
 *
 * v3.17.0 — Requirement #10.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Download, Upload, Users, Trash2, Crown, Loader2, Sparkles, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function streamDownload(res) {
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') || '';
  const m = dispo.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : `partner-scoring-${Date.now()}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PartnerScoringPanel({ pursuitId, primeName = 'Prime' }) {
  const { getAuthHeaders } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [primeDialogOpen, setPrimeDialogOpen] = useState(false);
  const [primeSourceName, setPrimeSourceName] = useState(primeName);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingFinal, setDownloadingFinal] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/submissions`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) return;
      const body = await res.json();
      setSubmissions(body.submissions || []);
    } catch (e) {
      console.error('submissions fetch failed', e);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        source_name: primeSourceName.trim() || 'Prime',
        source_role: 'prime',
      });
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/template.xlsx?${params}`,
        { headers: getAuthHeaders() },
      );
      if (res.status === 412) {
        const body = await res.json();
        toast.error(body?.detail?.message || 'No requirements yet — run the AI Shredder first.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await streamDownload(res);
      toast.success(`Template downloaded for ${primeSourceName}`);
      setPrimeDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Could not download template.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrimeUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_name', primeSourceName.trim() || 'Prime');
      fd.append('source_role', 'prime');
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/upload`,
        { method: 'POST', headers: getAuthHeaders(), body: fd },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      const skipped = body.skipped_invalid
        ? ` (${body.skipped_invalid} invalid rows skipped)` : '';
      toast.success(
        `${body.source_name}: ${body.rows_recorded} scores recorded${skipped}. ` +
        `Prime now leads on ${body.recompute.prime_wins} requirements.`,
      );
      fetchSubmissions();
      // Trigger a refetch on the requirements table by dispatching a custom event
      window.dispatchEvent(new CustomEvent('partner-scoring-changed', {
        detail: { pursuitId },
      }));
    } catch (e) {
      console.error(e);
      toast.error(`Upload failed: ${typeof e?.message === 'string' ? e.message : 'unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBatch = async (batchId, sourceName) => {
    if (!window.confirm(`Remove ${sourceName}'s submission? Their scores will be cleared.`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/submissions/${batchId}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Removed ${sourceName}'s submission`);
      fetchSubmissions();
      window.dispatchEvent(new CustomEvent('partner-scoring-changed', {
        detail: { pursuitId },
      }));
    } catch (e) {
      console.error(e);
      toast.error('Could not remove submission.');
    }
  };

  const handleConsolidatedDownload = async () => {
    setDownloadingFinal(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/partner-scoring/aggregate-export.xlsx`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await streamDownload(res);
      toast.success('Consolidated XLSX downloaded.');
    } catch (e) {
      console.error(e);
      toast.error('Download failed.');
    } finally {
      setDownloadingFinal(false);
    }
  };

  return (
    <Card
      className="border-amber-200 bg-amber-50/40 shadow-sm"
      data-testid="partner-scoring-panel"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2"
          style={{ fontFamily: 'Manrope, sans-serif' }}>
          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
            <Users className="w-4 h-4 text-amber-600" />
          </div>
          Partner Self-Scoring (XLSX round-trip)
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Download the requirements XLSX, share copies with your Prime and Teaming Partners,
          and upload each filled file back. The system keeps the highest score per requirement
          and attributes it to the Source.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action row */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="text-amber-700 border-amber-300 hover:bg-amber-100"
            onClick={() => setPrimeDialogOpen(true)}
            data-testid="partner-scoring-download-prime"
          >
            <Crown className="w-4 h-4 mr-1.5" />
            Download Prime Template
          </Button>

          <label
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-amber-300 text-amber-700 rounded-md hover:bg-amber-100 cursor-pointer"
            data-testid="partner-scoring-upload-prime"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Upload Prime's Filled File
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                handlePrimeUpload(f);
                e.target.value = '';
              }}
            />
          </label>

          <Button
            variant="outline"
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 ml-auto"
            onClick={handleConsolidatedDownload}
            disabled={downloadingFinal || submissions.length === 0}
            data-testid="partner-scoring-consolidated-export"
          >
            {downloadingFinal ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-1.5" />
            )}
            Download Consolidated Scores
          </Button>
        </div>

        {/* Empty state */}
        {submissions.length === 0 ? (
          <div className="flex items-start gap-2 p-3 bg-white border border-amber-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              No scoring responses uploaded yet. Start by downloading the Prime template,
              filling it locally, and uploading it back.
            </p>
          </div>
        ) : (
          <div
            className="bg-white border border-amber-200 rounded-md divide-y divide-amber-100"
            data-testid="partner-scoring-submissions-list"
          >
            {submissions.map((s, idx) => (
              <div
                key={s.batch_id}
                className="flex items-center justify-between p-3"
                data-testid={`partner-scoring-submission-${idx}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {s.source_role === 'prime' ? (
                    <Crown className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  ) : (
                    <Users className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">
                        {s.source_name}
                      </span>
                      <Badge
                        variant="outline"
                        className={s.source_role === 'prime'
                          ? 'bg-amber-50 text-amber-800 border-amber-300 text-[10px]'
                          : 'bg-purple-50 text-purple-800 border-purple-300 text-[10px]'}
                      >
                        {s.source_role.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.scored_count} scores · {s.submitted_by_email}
                      {s.last_submitted_at && (
                        <> · {new Date(s.last_submitted_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-red-600"
                  onClick={() => handleDeleteBatch(s.batch_id, s.source_name)}
                  data-testid={`partner-scoring-submission-delete-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Prime download dialog */}
      <Dialog open={primeDialogOpen} onOpenChange={setPrimeDialogOpen}>
        <DialogContent data-testid="partner-scoring-prime-dialog">
          <DialogHeader>
            <DialogTitle>Download Prime Template</DialogTitle>
            <DialogDescription>
              The Bid Manager scores on behalf of the Prime. Pick the name that should
              appear as the Source on the file and inside the matrix.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Prime Name</label>
            <Input
              value={primeSourceName}
              onChange={(e) => setPrimeSourceName(e.target.value)}
              placeholder="e.g. MNGC Group, Inc."
              data-testid="partner-scoring-prime-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrimeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDownloadTemplate}
              disabled={downloading || !primeSourceName.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="partner-scoring-prime-download-confirm"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              Download Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
